import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {DialogosDeLaCola} from '@/components/bateria/cola/dialogos-cola'
import {EncabezadoDeLaCola} from '@/components/bateria/cola/encabezado-cola'
import {RielSecciones} from '@/components/bateria/cola/riel-secciones'
import {BarraDeFiltros} from '@/components/bateria/cola/filtros-cola'
import {TarjetaEnCurso, campoDeTarjeta} from '@/components/bateria/cola/tarjeta-en-curso'
import type {FocoEscritura} from '@/components/bateria/cola/tarjeta-campo'
import type {GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {CierreLote, CierreSeccion} from '@/components/bateria/cola/cierre-seccion'
import {PreparandoCola, TarjetaEnCamino} from '@/components/bateria/cola/preparando-cola'
import {PosicionTarjeta} from '@/components/bateria/cola/posicion-tarjeta'
import {LoteLeyendo} from '@/components/bateria/cola/lote-leyendo'
import {useLoteLeyendo} from '@/components/bateria/cola/use-lote-leyendo'
import {useDescartarCuarentenaDeLaCola} from '@/components/bateria/cola/use-descartar-cuarentena-cola'
import {PilaDeRecortesProvider} from '@/components/bateria/cola/pila-de-recortes'
import {EvidenciaDeLaCola, useEvidenciaDeLaCola} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {useColaUbicar} from '@/components/bateria/cola/use-cola-ubicar'
import {useConflictosCola} from '@/components/bateria/cola/use-conflictos-cola'
import {mostrarCierreLote, useCursorCola} from '@/components/bateria/cola/use-cursor-cola'
import {useTransitoDeLaCola} from '@/components/bateria/cola/use-transito-cola'
import {useGestosCola} from '@/components/bateria/cola/use-gestos-cola'
import {useSwipeParaDespues} from '@/components/bateria/cola/use-swipe-para-despues'
import {useTeclasCola} from '@/components/bateria/cola/use-teclas-cola'
import {FrenoLote} from '@/components/bateria/cola/freno-lote'
import {
  AvisoCargandoTramos,
  AvisoColaRecortada,
  AvisoConflictoActual,
  AvisoConflictos,
  AvisoDescarteHecho,
  AvisoErrorCarga,
  AvisoErrorTramos,
  AvisoFiltroVacio,
  AvisoRecuperando,
  AvisoSincronizacion,
} from '@/components/bateria/cola/avisos-cola'
import {useEvaluacionDetail, useFicha} from '@/hooks/bateria/use-bateria'
import {useCuarentenaCandidatos, useRestaurarCuarentena} from '@/hooks/bateria/use-cuarentena'
import {useEnvioDecisiones} from '@/hooks/bateria/use-envio-decisiones'
import {
  useAplicarDecisiones,
  useColaRevision,
  useResumenRevision,
  useValidarLote,
  type DecisionesAplicadas,
  type ValidacionMasiva,
} from '@/hooks/bateria/use-bateria-cola'
import {
  avancesDeSecciones,
  borradorDeAntiguedad,
  decisionBase,
  decisionesDelServidor,
  loteDegradado,
  paqueteDeRef,
  resolverCandidato,
  seleccionDeCampo,
  tarjetasDeSeccion,
  totalesDeSecciones,
  type DecisionCola,
  type DecisionLocal,
  type TarjetaCola,
} from '@/lib/bateria-cola'
import {cuantasPorMarcar, esPorMarcar} from '@/lib/cola-estados'
import {clavePendientes} from '@/lib/cola-pendientes'
import {
  almacenDelAparato,
  claveDejadas,
  dejar as dejarEnElAparato,
  leerDejadas,
  olvidarDejada as olvidarDejadaDelAparato,
} from '@/lib/cola-dejadas'
import {
  MAX_REFS_EN_URL,
  filtroDejoLaColaEnCero,
  filtrosTrasVaciarFiltro,
  pedidoDeCola,
  sinFiltrosActivos,
  urlDeFiltros,
  type FiltrosCola,
} from '@/lib/cola-filtros'
import {publicarAviso} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'

/**
 * Se monta con `key={firmaDeFiltros(filtros)}`: remontar es lo que arranca `useCursorCola` sobre la cola nueva.
 * Decisiones y dejadas sobreviven en el aparato, así que cambiar de filtro no pide confirmación.
 */
export function ColaEnCurso({
  businessId,
  evaluacionId,
  filtros,
  volverA,
  rutaCola,
  sujeto,
  avisoDeColaNueva,
  onCursorMovido,
}: {
  businessId: string
  evaluacionId: string
  filtros: FiltrosCola
  volverA: string
  /** Esta pantalla sin query: base para escribir los filtros. */
  rutaCola: string
  sujeto: string
  /** Lo decide la página, que sobrevive al remontaje por cambio de filtro. */
  avisoDeColaNueva: boolean
  onCursorMovido: () => void
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const almacen = useMemo(() => almacenDelAparato(), [])
  const claveDeDejadas = claveDejadas(businessId, evaluacionId)
  // Congeladas al entrar: leídas en vivo, la cola se encogería bajo la mano a cada respuesta.
  const [refsDejadas] = useState<string[]>(() => (almacen ? leerDejadas(almacen, claveDeDejadas) : []))

  const {data: resumen} = useResumenRevision(businessId, evaluacionId, true)
  const [ignorarFreno, setIgnorarFreno] = useState(false)
  const frenado = loteDegradado(resumen) && !ignorarFreno
  const {lotes, loteLeyendo} = useLoteLeyendo(businessId, evaluacionId)
  // `null` = desde el principio; con valor, una vuelta siguiente de un repaso recortado.
  const [desde, setDesde] = useState<string | null>(null)
  const pedido = useMemo(() => pedidoDeCola(filtros, refsDejadas), [filtros, refsDejadas])
  const {
    data: cola,
    cargandoTramos,
    recortada,
    error: errorCarga,
    reintentar: reintentarCarga,
    errorTramos,
    reintentarTramos,
  } = useColaRevision(businessId, evaluacionId, pedido, resumen !== undefined && !frenado && !loteLeyendo, desde)
  const {data: detalle} = useEvaluacionDetail(businessId, evaluacionId, true)

  const [decisiones, setDecisiones] = useState<Record<string, DecisionLocal>>({})
  // En memoria para el riel y el cursor; se espeja al aparato (`lib/cola-dejadas.ts`).
  const [dejadas, setDejadas] = useState<Record<string, true>>(() =>
    Object.fromEntries(refsDejadas.map((ref) => [ref, true as const]))
  )
  const [gesto, setGesto] = useState<GestoEnCurso>(null)
  const [borrador, setBorrador] = useState('')
  // Vive en la página porque lo mueve el teclado global de la cola.
  const [foco, setFoco] = useState<FocoEscritura>('suelto')
  const [mapaAbierto, setMapaAbierto] = useState(false)
  const [salidaConPendientes, setSalidaConPendientes] = useState(false)
  const [validacion, setValidacion] = useState<ValidacionMasiva | null>(null)
  const [descartada, setDescartada] = useState<string | null>(null)

  const aplicar = useAplicarDecisiones(businessId, evaluacionId)
  const restaurar = useRestaurarCuarentena(businessId, evaluacionId)
  const validar = useValidarLote(businessId, evaluacionId)

  const paquetes = useMemo(
    () => (detalle?.paquetes ?? []).map((p) => ({id: p.id, employee_name: p.employee_name, forma: p.forma as string})),
    [detalle]
  )

  const {buscando, setBuscando, destino, descarteArmado, armarODescartar, limpiar, seleccionarCandidato} =
    useColaUbicar(paquetes)

  const tarjetas = useMemo(() => cola?.tarjetas ?? [], [cola])
  // Tamaños del servidor y no de lo cargado: la cola llega por tramos y el riel crecería bajo la mano.
  // Antes del primer tramo se usa `pendientes` del resumen; `total` pintaría un riel que encoge.
  const totales = useMemo(
    () => (cola ? totalesDeSecciones(cola.secciones) : totalesDeSecciones(resumen?.por_seccion ?? [], 'pendientes')),
    [cola, resumen]
  )

  const resuelta = useCallback((ref: string) => ref in decisiones || ref in dejadas, [decisiones, dejadas])
  const porMarcar = useCallback((tarjeta: TarjetaCola) => esPorMarcar(tarjeta, resuelta), [resuelta])

  const alMover = useCallback(() => {
    setGesto(null)
    limpiar()
    onCursorMovido()
  }, [limpiar, onCursorMovido])

  const {
    seccion,
    indice,
    fase,
    deLaSeccion,
    totalDeLaSeccion,
    actual,
    siguienteSeccion,
    enRonda,
    avanzar,
    atras,
    adelante,
    entrarASeccion,
    entrarARonda,
    irATarjeta,
    saltarA,
    continuar,
    reiniciar,
    irASiguientePorMarcar,
  } = useCursorCola({cola, tarjetas, totales, decisiones, dejadas, porMarcar, seccionInicial: filtros.irA, alMover})

  // `←`, `→` y el avance tras decidir esperan a que el papel del destino esté listo; los saltos largos no.
  const transito = useTransitoDeLaCola({
    businessId,
    evaluacionId,
    deLaSeccion,
    indice,
    atras,
    adelante,
    avanzar,
  })

  // G14: los candidatos del pie no viajan en la tarjeta (leerlos rasteriza el PDF); se piden por hoja.
  const hojaEnCurso = actual?.tipo === 'ubicar' ? (actual.ref.split(':')[1] ?? '') : ''
  const {data: propuestos, isLoading: buscandoCandidatos} = useCuarentenaCandidatos(
    businessId,
    evaluacionId,
    hojaEnCurso,
    hojaEnCurso !== ''
  )
  // Solo paquetes vivos del roster: uno retirado o reasignado entre la lectura y el clic sería asignar a ciegas.
  const candidatos = useMemo(
    () => (propuestos ?? []).filter((candidato) => resolverCandidato(candidato, paquetes) !== null),
    [propuestos, paquetes]
  )

  // Sobre la cola congelada: los conteos del riel no pueden divergir de lo que la pantalla entrega.
  const avances = useMemo(
    () => avancesDeSecciones(tarjetas, decisiones, dejadas, totales),
    [tarjetas, decisiones, dejadas, totales]
  )
  const globalTotal = avances.reduce((suma, avance) => suma + avance.total, 0)
  const globalHechas = avances.reduce((suma, avance) => suma + avance.hechas, 0)
  const globalDejadas = avances.reduce((suma, avance) => suma + avance.dejadas, 0)
  const globalEnConflicto = avances.reduce((suma, avance) => suma + avance.enConflicto, 0)

  // Lo decidido en el servidor entra debajo de lo de esta sesión (`...previas` gana); sin esto,
  // volver a entrar re-preguntaba todo.
  useEffect(() => {
    if (!cola) return
    const sembradas = decisionesDelServidor(cola.tarjetas)
    if (Object.keys(sembradas).length === 0) return
    setDecisiones((previas) => ({...sembradas, ...previas}))
  }, [cola])

  // `mutate` es estable (react-query) y el objeto de la mutación no: así los `useCallback` no se rehacen.
  const aplicarDecisiones = aplicar.mutate
  const restaurarReset = restaurar.reset
  const descartarCuarentena = useDescartarCuarentenaDeLaCola(
    businessId,
    evaluacionId,
    restaurarReset,
    setDescartada,
    setDecisiones
  )

  const alAplicar = useCallback((envio: DecisionCola[], conflictos: DecisionesAplicadas['conflictos']) => {
    setDecisiones((previas) => {
      const siguientes = {...previas}
      for (const decision of envio) {
        const local = siguientes[decision.ref]
        if (local) siguientes[decision.ref] = {...local, estado: 'enviada'}
      }
      for (const conflicto of conflictos) {
        const local = siguientes[conflicto.ref]
        if (local) {
          siguientes[conflicto.ref] = {
            ...local,
            estado: 'conflicto',
            decididoPor: conflicto.decidido_por,
            decididoPorNombre: conflicto.decidido_por_nombre,
            decididoEn: conflicto.decidido_en,
            // Lo tecleado sigue en `decision`: es lo que ofrece «cambiar por la mía».
            ganador: conflicto.valor_ganador,
          }
        }
      }
      return siguientes
    })
  }, [])

  const {
    porSincronizar,
    falloEnvio,
    reintentoEn,
    reintentoAgotado,
    envioDemorado,
    recuperando,
    encolar,
    reintentarAhora,
  } = useEnvioDecisiones(aplicarDecisiones, alAplicar, clavePendientes(businessId, evaluacionId))
  const hayEnVuelo = porSincronizar > 0

  const olvidarDejada = useCallback(
    (ref: string) => {
      if (almacen) olvidarDejadaDelAparato(almacen, claveDeDejadas, ref)
      setDejadas((previas) => {
        if (!(ref in previas)) return previas
        const siguientes = {...previas}
        delete siguientes[ref]
        return siguientes
      })
    },
    [almacen, claveDeDejadas]
  )

  const registrar = useCallback(
    (tarjeta: TarjetaCola, parcial: Partial<DecisionCola>) => {
      const decision: DecisionCola = {...decisionBase(tarjeta), ...parcial}
      setDecisiones((previas) => ({...previas, [tarjeta.ref]: {decision, estado: 'pendiente'}}))
      olvidarDejada(tarjeta.ref)
      encolar([decision])
      transito.avanzar()
    },
    [encolar, transito, olvidarDejada]
  )

  const dejarParaDespues = useCallback(
    (tarjeta: TarjetaCola) => {
      if (almacen) dejarEnElAparato(almacen, claveDeDejadas, tarjeta.ref)
      setDejadas((previas) => ({...previas, [tarjeta.ref]: true}))
      transito.avanzar()
    },
    [almacen, claveDeDejadas, transito]
  )

  const {irAlPrimerConflicto, dejarConflictoAsi, cambiarPorLaMia} = useConflictosCola({
    tarjetas,
    decisiones,
    setDecisiones,
    irATarjeta,
    avanzar: transito.avanzar,
    encolar,
  })

  const {
    elegirMarca,
    cerrarGesto,
    tachonSinRespuesta,
    elegirCampo,
    senalarCasilla,
    clicSenala,
    asignarHoja,
    descartarHoja,
  } = useGestosCola({
    gesto,
    setGesto,
    decisiones,
    setDecisiones,
    registrar,
    avanzar: transito.avanzar,
    olvidarDejada,
    descartarCuarentena,
  })

  // El catálogo de la ficha es estático: se pide el de un paquete de la sección y sirve para todas.
  const paqueteConFicha = useMemo(
    () => paqueteDeRef(tarjetasDeSeccion(tarjetas, 'confirmar')[0]?.ref ?? '') ?? undefined,
    [tarjetas]
  )
  const {data: fichaCatalogo} = useFicha(businessId, paqueteConFicha, !!paqueteConFicha)
  const opcionesDeCampo = useCallback(
    (campo: string) => fichaCatalogo?.catalogo.find((meta) => meta.id === campo)?.opciones ?? [],
    [fichaCatalogo]
  )

  // `Enter` deja el valor propuesto seleccionado; una tecla imprimible es el valor y el cursor va detrás.
  const comenzarAEscribir = useCallback((inicial: string | null) => {
    if (inicial !== null) setBorrador(inicial)
    setFoco(inicial === null ? 'campo' : 'campo-al-final')
  }, [])

  // Sin la evidencia de esta tarjeta pintada no se decide, ni por teclado ni por botón; navegar, `D` y `Esc` siempre.
  const papel = useEvidenciaDeLaCola({
    businessId,
    evaluacionId,
    actual,
    deLaSeccion,
    indice,
    enTransito: transito.enTransito,
  })

  useTeclasCola({
    fase,
    actual,
    puedeDecidir: papel.puedeDecidir,
    alDecidirSinPapel: papel.pulsar,
    evidenciaALaVista: papel.evidenciaALaVista,
    seleccionActual: actual ? seleccionDeCampo(decisiones[actual.ref]) : null,
    gesto,
    destino,
    descarteArmado,
    setGesto,
    setBuscando,
    setMapaAbierto,
    limpiarUbicar: limpiar,
    atras: transito.atras,
    adelante: transito.adelante,
    continuar,
    dejarParaDespues,
    descartarHoja,
    armarODescartar,
    asignarHoja,
    cerrarGesto,
    tachonSinRespuesta,
    registrar,
    elegirCampo,
    comenzarAEscribir,
    opcionesDeCampo,
    candidatos,
    seleccionarCandidato,
    irASiguientePorMarcar,
  })

  // El valor entra puesto pero el foco no: las flechas siguen navegando y la tarjeta no hereda el modo escritura.
  useEffect(() => {
    setFoco('suelto')
    if (actual?.tipo === 'escribir') setBorrador(actual.valor_propuesto ?? '')
    // Lo ya decidido pisa la propuesta del OCR: «menos de un año» es una opción, no una cifra para la celda.
    if (actual?.tipo === 'antiguedad') setBorrador(borradorDeAntiguedad(actual, decisiones[actual.ref]))
  }, [actual]) // eslint-disable-line react-hooks/exhaustive-deps -- decisiones se lee al vuelo: solo importa la que ya estaba lista cuando `actual` cambió, no re-sembrar el borrador mientras la persona escribe (un sync en curso no puede pisarle lo que está tecleando)

  const swipe = useSwipeParaDespues(actual, fase === 'tarjetas', dejarParaDespues)

  // El foco sigue a la tarjeta en las cinco secciones, `escribir` incluida: entrar al campo es un acto aparte.
  const contenedorTarjeta = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (fase !== 'tarjetas' || !actual) return
    contenedorTarjeta.current?.focus({preventScroll: true})
  }, [actual, fase])

  const validables = filtros.paqueteIds.length > 0 ? filtros.paqueteIds.length : paquetes.length
  const vivas = tarjetas.filter((tarjeta) => !resuelta(tarjeta.ref)).length
  // Lo que S0 produce después de sembrar va al final, nunca bajo el cursor (§1.7); solo sin filtro.
  const sinFiltro = sinFiltrosActivos(filtros)
  const segundaVuelta = sinFiltro ? Math.max(0, (resumen?.pendientes ?? 0) - vivas) : 0
  const filtroVacio = filtroDejoLaColaEnCero(filtros, !!cola, globalTotal)
  const pctFiltroVacio = filtros.confianzaMax !== null ? Math.round(filtros.confianzaMax * 100) : null
  const quitarFiltroVacio = useCallback(
    () => navigate(`${rutaCola}${urlDeFiltros(filtrosTrasVaciarFiltro(filtros))}`),
    [filtros, navigate, rutaCola]
  )

  // El servidor encadena el informe al validar (`POST .../validar`); la respuesta la pinta `ValidacionResultado`.
  function validarLote() {
    setValidacion(null)
    validar.mutate(filtros.paqueteIds.length > 0 ? filtros.paqueteIds : undefined, {
      onSuccess: setValidacion,
      // La red global no cubre esta mutación (`manejaError`) y `CierreLote` solo ve `validando`: sin esto falla muda.
      onError: () =>
        publicarAviso(
          t('admin.bateria.cola.lote.validar', {count: validables}),
          t('admin.bateria.siguientePaso.validarError')
        ),
    })
  }

  function reiniciarCola() {
    queryClient.invalidateQueries({queryKey: ['bateria-cola', businessId, evaluacionId]})
    reiniciar()
  }

  /** Siembra una cola nueva desde el cursor vivo; lo decidido llega marcado y no se re-pregunta. */
  function traerSiguientes() {
    if (!cola?.next_cursor) return
    setDesde(cola.next_cursor)
    setDecisiones({})
    // Las dejadas no se limpian: viven en el aparato y la segunda vuelta es la misma evaluación.
    reiniciar()
  }

  function aceptarEnBloque(pendientesDeBloque: TarjetaCola[]) {
    const nuevas = pendientesDeBloque.map((tarjeta) => ({
      ...decisionBase(tarjeta),
      valor: tarjeta.valor_propuesto,
      fuente: 'confirmado_bloque' as const,
    }))
    setDecisiones((previas) => {
      const siguientes = {...previas}
      for (const decision of nuevas) siguientes[decision.ref] = {decision, estado: 'pendiente'}
      return siguientes
    })
    encolar(nuevas)
  }

  const enCurso = seccion ? tarjetasDeSeccion(tarjetas, seccion) : []
  // Una tarjeta mirada y dejada adrede no entra al bloque: el gesto dice «una propuesta que nadie tocó».
  const paraBloque = enCurso.filter(
    (tarjeta) => !(tarjeta.ref in decisiones) && !(tarjeta.ref in dejadas) && tarjeta.valor_propuesto !== null
  )
  const conflictoActual =
    fase === 'tarjetas' && actual && decisiones[actual.ref]?.estado === 'conflicto' ? decisiones[actual.ref] : null
  const posicion = (
    <PosicionTarjeta
      numero={indice + 1}
      total={totalDeLaSeccion}
      enRonda={enRonda}
      onIr={saltarA}
      porMarcar={cuantasPorMarcar(tarjetas, resuelta)}
      onSiguientePorMarcar={irASiguientePorMarcar}
    />
  )

  return (
    <>
      <EncabezadoDeLaCola
        sujeto={sujeto}
        onVolver={() => (porSincronizar > 0 ? setSalidaConPendientes(true) : navigate(volverA))}
        onAbrirMapa={() => setMapaAbierto(true)}
      />

      <BarraDeFiltros
        filtros={filtros}
        businessId={businessId}
        evaluacionId={evaluacionId}
        pendientes={resumen?.pendientes ?? 0}
        dejadas={Object.keys(dejadas).length}
        sujeto={sujeto}
        paquetes={detalle?.paquetes ?? []}
        onCambiar={(cambio) => navigate(`${rutaCola}${urlDeFiltros(cambio)}`)}
      />

      {loteLeyendo ? (
        <LoteLeyendo lotes={lotes} volverA={volverA} />
      ) : frenado && resumen ? (
        <FrenoLote resumen={resumen} volverA={volverA} onRevisarIgual={() => setIgnorarFreno(true)} />
      ) : (
        <>
          <RielSecciones
            avances={avances}
            activa={seccion}
            navegable={!!cola}
            sujeto={sujeto}
            globalHechas={globalHechas}
            globalTotal={globalTotal}
            globalDejadas={globalDejadas}
            globalEnConflicto={globalEnConflicto}
            onIrASeccion={entrarASeccion}
          />

          {avisoDeColaNueva && cola && (
            <p className="px-1 font-mono text-[11px] text-muted-foreground" data-testid="cola-filtros-aviso">
              {t('admin.bateria.cola.filtros.avisoColaNueva', {n: globalTotal})}
            </p>
          )}

          {filtros.revisar === 'dejadas' && refsDejadas.length > MAX_REFS_EN_URL && (
            <p className="notice-amber rounded-lg px-4 py-3 text-sm" data-testid="cola-dejadas-recortadas">
              {t('admin.bateria.cola.filtros.recorte', {max: MAX_REFS_EN_URL, total: refsDejadas.length})}
            </p>
          )}

          {cargandoTramos && <AvisoCargandoTramos cargadas={tarjetas.length} total={globalTotal} />}

          {errorTramos && <AvisoErrorTramos onReintentar={reintentarTramos} />}

          {recortada && <AvisoColaRecortada cargadas={tarjetas.length} onTraerSiguientes={traerSiguientes} />}

          {globalEnConflicto > 0 && <AvisoConflictos total={globalEnConflicto} onVerConflictos={irAlPrimerConflicto} />}

          {conflictoActual && actual && (
            <AvisoConflictoActual
              conflicto={conflictoActual}
              tarjeta={actual}
              onDejar={() => dejarConflictoAsi(actual)}
              onCambiar={() => cambiarPorLaMia(actual)}
            />
          )}

          {descartada && (
            <AvisoDescarteHecho
              pending={restaurar.isPending}
              error={restaurar.isError ? translateApiError(restaurar.error) : null}
              onDeshacer={() => restaurar.mutate(descartada, {onSuccess: () => setDescartada(null)})}
            />
          )}

          {/* La pila de recortes se monta acá y no por tipo de tarjeta (`pila-de-recortes.tsx`). */}
          <PilaDeRecortesProvider tarjetas={deLaSeccion} indice={indice}>
            <EvidenciaDeLaCola valor={papel.contexto}>
              <div
                ref={contenedorTarjeta}
                tabIndex={-1}
                role="group"
                aria-live="polite"
                aria-label={t('admin.bateria.cola.tarjetaEnCurso')}
                className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                {...swipe}>
                {!cola ? (
                  errorCarga ? (
                    <AvisoErrorCarga onReintentar={reintentarCarga} />
                  ) : (
                    <PreparandoCola tarjetas={resumen?.pendientes ?? 0} personas={paquetes.length} />
                  )
                ) : filtroVacio ? (
                  <AvisoFiltroVacio pct={pctFiltroVacio} onQuitar={quitarFiltroVacio} />
                ) : mostrarCierreLote(fase, cargandoTramos, errorTramos) ? (
                  <CierreLote
                    decididas={globalHechas}
                    dejadas={globalDejadas}
                    sinRecorte={resumen?.sin_recorte ?? 0}
                    calibracion={resumen?.calibracion_motor ?? null}
                    paquetesValidables={validables}
                    onValidar={validarLote}
                    validando={validar.isPending}
                    resultado={validacion}
                    nombreDePaquete={(id) =>
                      paquetes.find((p) => p.id === id)?.employee_name ??
                      t('admin.bateria.validacion.personaDesconocida')
                    }
                    segundaVuelta={segundaVuelta}
                    onSegundaVuelta={reiniciarCola}
                    pendientesDelLote={resumen?.pendientes ?? 0}
                    volverA={volverA}
                  />
                ) : fase === 'cierre' && seccion ? (
                  <CierreSeccion
                    seccion={seccion}
                    siguiente={siguienteSeccion}
                    decididas={avances.find((a) => a.clave === seccion)?.hechas ?? 0}
                    dejadas={avances.find((a) => a.clave === seccion)?.dejadas ?? 0}
                    pendientesDeBloque={paraBloque}
                    totalSiguiente={siguienteSeccion ? tarjetasDeSeccion(tarjetas, siguienteSeccion).length : 0}
                    onVolverADejadas={entrarARonda}
                    onAceptarBloque={() => aceptarEnBloque(paraBloque)}
                    onContinuar={continuar}
                  />
                ) : actual && seccion ? (
                  <TarjetaEnCurso
                    tarjeta={actual}
                    seccion={seccion}
                    posicion={posicion}
                    decision={decisiones[actual.ref]}
                    gesto={gesto}
                    businessId={businessId}
                    evaluacionId={evaluacionId}
                    opciones={opcionesDeCampo(campoDeTarjeta(actual))}
                    paquetes={paquetes}
                    puedeDecidir={papel.puedeDecidir}
                    clicSenala={clicSenala}
                    ubicar={{
                      destino,
                      candidatos,
                      buscandoCandidatos,
                      descarteArmado: descarteArmado === actual.ref,
                      buscando,
                      pendiente: aplicar.isPending,
                      onElegirCandidato: (i) => seleccionarCandidato(actual, i, candidatos),
                      onAsignar: (elegido) => asignarHoja(actual, elegido),
                      onDescartar: () => armarODescartar(actual, descartarHoja),
                      onBuscar: setBuscando,
                    }}
                    escritura={{
                      borrador,
                      foco,
                      onEscribir: () => comenzarAEscribir(null),
                      onCambiar: setBorrador,
                      onSoltarFoco: () => setFoco('suelto'),
                    }}
                    nav={{
                      onDespues: () => dejarParaDespues(actual),
                      onAtras: transito.atras,
                      onAdelante: transito.adelante,
                    }}
                    onElegirMarca={(valor) => elegirMarca(actual, valor)}
                    onTachonSinRespuesta={() => tachonSinRespuesta(actual)}
                    onElegirCampo={(valor) => elegirCampo(actual, valor)}
                    onSenalarCasilla={senalarCasilla}
                  />
                ) : fase === 'tarjetas' && indice < totalDeLaSeccion ? (
                  // El tramo de esta posición no llegó: solo se alcanza con un «Ir a #» adelantado.
                  <TarjetaEnCamino numero={indice + 1} />
                ) : (
                  <p className="surface px-5 py-6 text-sm text-muted-foreground" data-testid="cola-vacia">
                    {t('admin.bateria.cola.vacia')}
                  </p>
                )}
              </div>
            </EvidenciaDeLaCola>
          </PilaDeRecortesProvider>

          {(recuperando > 0 || (hayEnVuelo && (envioDemorado || falloEnvio))) && (
            <div className="sticky bottom-2 z-30 space-y-1 max-md:bottom-24" data-testid="cola-franja-estado">
              {recuperando > 0 && <AvisoRecuperando cuantas={recuperando} />}
              {hayEnVuelo && (envioDemorado || falloEnvio) && (
                <AvisoSincronizacion
                  porSincronizar={porSincronizar}
                  reintentoAgotado={reintentoAgotado}
                  reintentoEn={reintentoEn}
                  onReintentar={reintentarAhora}
                />
              )}
            </div>
          )}
        </>
      )}

      <DialogosDeLaCola
        salidaConPendientes={salidaConPendientes}
        porSincronizar={porSincronizar}
        onSalidaOpenChange={setSalidaConPendientes}
        onSalir={() => {
          setSalidaConPendientes(false)
          navigate(volverA)
        }}
        mapaAbierto={mapaAbierto}
        onMapaOpenChange={setMapaAbierto}
        seccion={seccion}
      />
    </>
  )
}
